import axios from 'axios';

import { ErrorTracker } from './errorTracking';

interface AxiosError extends Error {
  config?: any;
  code?: string;
  request?: any;
  response?: AxiosResponse;
  isAxiosError: boolean;
}

interface AxiosResponse {
  data: any;
  status: number;
  statusText: string;
  headers: any;
  config: any;
}

// Enhanced axios interceptor for comprehensive network error tracking
export const setupNetworkErrorTracking = () => {
  // Request interceptor to log outgoing requests
  (axios as any).interceptors.request.use(
    (config: any) => {
      // Add timestamp to track request duration
      config.metadata = { startTime: Date.now() };
      
      // Add breadcrumb for outgoing request
      if (window.Sentry) {
        window.Sentry.addBreadcrumb({
          category: 'http',
          message: `HTTP ${config.method?.toUpperCase()} ${config.url}`,
          level: 'info',
          data: {
            method: config.method,
            url: config.url,
            baseURL: config.baseURL,
          },
        });
      }
      
      return config;
    },
    (error: any) => {
      ErrorTracker.trackNetworkError(error, {
        url: error.config?.url || 'unknown',
        method: error.config?.method || 'unknown',
        status: error.response?.status,
        statusText: error.response?.statusText,
      });

      return Promise.reject(error);
    },
  );

  // Response interceptor to track responses and errors
  (axios as any).interceptors.response.use(
    (response: AxiosResponse) => {
      const duration = response.config.metadata?.startTime 
        ? Date.now() - response.config.metadata.startTime 
        : 0;
      
      // Track slow API calls (>5 seconds)
      if (duration > 5000) {
        ErrorTracker.trackPerformanceIssue({
          type: 'slow_load',
          metric: 'api_response_time',
          value: duration,
          threshold: 5000,
          details: {
            url: response.config.url,
            method: response.config.method,
            status: response.status,
            duration,
          },
        });
      }
      
      // Add successful response breadcrumb
      if (window.Sentry) {
        window.Sentry.addBreadcrumb({
          category: 'http',
          message: `HTTP ${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status}`,
          level: 'info',
          data: {
            status: response.status,
            duration,
            size: JSON.stringify(response.data).length,
          },
        });
      }
      
      return response;
    },
    (error: AxiosError) => {
      const duration = error.config?.metadata?.startTime 
        ? Date.now() - error.config.metadata.startTime 
        : 0;
      
      // Determine error type and context
      const errorContext = {
        url: error.config?.url || 'unknown',
        method: error.config?.method || 'unknown',
        status: error.response?.status,
        statusText: error.response?.statusText,
        headers: error.response?.headers,
        data: error.response?.data,
        duration,
      };
      
      // Add specific error categorization
      const errorType = categorizeNetworkError(error);
      
      // Track the network error with detailed context
      ErrorTracker.trackNetworkError(error, {
        ...errorContext,
        errorType,
        isTimeout: error.code === 'ECONNABORTED',
        isNetworkError: !error.response,
        isServerError: error.response?.status ? error.response.status >= 500 : false,
        isClientError: error.response?.status ? error.response.status >= 400 && error.response.status < 500 : false,
      });
      
      return Promise.reject(error);
    },
  );
};

// Categorize network errors for better tracking
function categorizeNetworkError(error: AxiosError): string {
  if (error.code === 'ECONNABORTED') {
    return 'timeout';
  }
  
  if (!error.response) {
    return 'network_error';
  }
  
  const status = error.response.status;
  
  if (status >= 500) {
    return 'server_error';
  }
  
  if (status === 401) {
    return 'authentication_error';
  }
  
  if (status === 403) {
    return 'authorization_error';
  }
  
  if (status === 404) {
    return 'not_found_error';
  }
  
  if (status === 422) {
    return 'validation_error';
  }
  
  if (status >= 400) {
    return 'client_error';
  }
  
  return 'unknown_error';
}

// Fetch API error tracking (for non-axios requests)
export const setupFetchErrorTracking = () => {
  const originalFetch = window.fetch;
  
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const startTime = Date.now();
    const url = typeof input === 'string' ? input : input.toString();
    const method = init?.method || 'GET';
    
    try {
      const response = await originalFetch(input, init);
      const duration = Date.now() - startTime;
      
      // Track slow fetch requests
      if (duration > 5000) {
        ErrorTracker.trackPerformanceIssue({
          type: 'slow_load',
          metric: 'fetch_response_time',
          value: duration,
          threshold: 5000,
          details: {
            url,
            method,
            status: response.status,
            duration,
          },
        });
      }
      
      // Track fetch errors (4xx, 5xx)
      if (!response.ok) {
        const error = new Error(`Fetch failed: ${response.status} ${response.statusText}`);
        ErrorTracker.trackNetworkError(error, {
          url,
          method,
          status: response.status,
          statusText: response.statusText,
          duration,
        });
      }
      
      return response;
    } catch (error) {
      const duration = Date.now() - startTime;
      ErrorTracker.trackNetworkError(error, {
        url,
        method,
        duration,
      });
      throw error;
    }
  };
};

// WebSocket error tracking
export const trackWebSocketErrors = (ws: WebSocket, identifier: string) => {
  ws.addEventListener('error', (_event) => {
    ErrorTracker.trackNetworkError(
      new Error('WebSocket error'),
      {
        url: ws.url,
        method: 'WebSocket',
        identifier,
      },
    );
  });
  
  ws.addEventListener('close', (event) => {
    // Not a normal closure
    if (event.code !== 1000) {
      ErrorTracker.trackNetworkError(
        new Error(`WebSocket closed unexpectedly: ${event.code} ${event.reason}`),
        {
          url: ws.url,
          method: 'WebSocket',
          status: event.code,
          statusText: event.reason,
          identifier,
        },
      );
    }
  });
};

// GraphQL error tracking
export const trackGraphQLErrors = (response: any, query: string, variables?: any) => {
  if (response.errors && response.errors.length > 0) {
    response.errors.forEach((error: any, index: number) => {
      ErrorTracker.trackNetworkError(
        new Error(`GraphQL Error: ${error.message}`),
        {
          url: 'GraphQL',
          method: 'POST',
          data: {
            // Truncate long queries
            query: query.substring(0, 200),
            variables,
            errorIndex: index,
            errorPath: error.path,
            errorExtensions: error.extensions,
          },
        },
      );
    });
  }
}; 